/**
 * Protected Route Component
 * Redirects unauthenticated users to login page
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import ShieldIcon from '../ShieldIcon';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/** Shared with App Suspense while the dashboard chunk loads after navigation to /app. */
export const DashboardRouteFallback: React.FC = () => (
    <div className="dashboard-layout bg-secondary-50 dark:bg-secondary-900">
        {/* Match SideRail chrome (white rail + border in light mode) so login → app does not flash a dark strip */}
        <div className="sidebar-fixed flex flex-col items-center border-r border-secondary-200 bg-white py-5 dark:border-transparent dark:bg-secondary-900">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden">
                <ShieldIcon className="w-10 h-10" />
            </div>
        </div>
        <div className="main-content-area">
            <div className="center-content">
                <div className="flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-brand-500 mr-3" />
                    <p className="text-secondary-500 text-sm font-medium">Loading...</p>
                </div>
            </div>
        </div>
    </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // Not authenticated - redirect to login
    if (!isLoading && !isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Auth still resolving: show shell only. Do not mount the dashboard here — the previous
    // hidden pre-render forced a full Dashboard mount during auth, then a remount when the wrapper
    // changed, doubling work on the critical path after login.
    if (isLoading) {
        return <DashboardRouteFallback />;
    }

    // Authenticated and loaded - show dashboard (loaded via React.lazy + Suspense in App)
    return <>{children}</>;
};

export default ProtectedRoute;
